import { Injectable, inject, signal, computed } from '@angular/core';
import { Router } from '@angular/router';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { ConfigService } from './config.service';

export interface User {
  name: string;
  email: string;
  avatar?: string;
  picture?: string;
  status: 'approved';
}

const TOKEN_STORAGE_KEY = 'pms_auth_token';
const USER_STORAGE_KEY = 'pms_auth_user';

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private router = inject(Router);
  private http = inject(HttpClient);
  private configService = inject(ConfigService);
  private userSignal = signal<User | null>(null);
  private sessionLoaded = false;
  private loadingPromise: Promise<void> | null = null;
  private backgroundVerifyDone = false;

  user = this.userSignal.asReadonly();
  isAuthenticated = computed(() => this.userSignal() !== null);

  constructor() {
    this.extractTokenFromUrl();
    this.restoreCachedUser();
  }

  private extractTokenFromUrl(): boolean {
    if (typeof window === 'undefined') {
      return false;
    }

    const urlParams = new URLSearchParams(window.location.search);
    const token = urlParams.get('token');

    if (token) {
      localStorage.setItem(TOKEN_STORAGE_KEY, token);

      urlParams.delete('token');
      const newUrl =
        window.location.pathname +
        (urlParams.toString() ? `?${urlParams.toString()}` : '') +
        window.location.hash;
      window.history.replaceState({}, '', newUrl);

      // Angular Router can keep ?token= in its snapshot after replaceState — drop it so
      // queryParams subscribers don't re-run ensureSession on unrelated navigations.
      void this.router.navigate([], {
        queryParams: { token: null },
        queryParamsHandling: 'merge',
        replaceUrl: true,
      });

      return true;
    }

    return false;
  }

  getToken(): string | null {
    return localStorage.getItem(TOKEN_STORAGE_KEY);
  }

  private clearToken(): void {
    localStorage.removeItem(TOKEN_STORAGE_KEY);
  }

  private persistUser(user: User): void {
    const payload = JSON.stringify(user);
    try {
      localStorage.setItem(USER_STORAGE_KEY, payload);
      sessionStorage.setItem(USER_STORAGE_KEY, payload);
    } catch {
      // Ignore storage quota / private mode errors.
    }
  }

  private restoreCachedUser(): void {
    if (this.userSignal()) {
      return;
    }

    for (const storage of [localStorage, sessionStorage]) {
      try {
        const raw = storage.getItem(USER_STORAGE_KEY);
        if (!raw) {
          continue;
        }
        const parsed = JSON.parse(raw);
        if (parsed?.email && parsed?.status === 'approved') {
          const cached = parsed as User;
          this.userSignal.set({
            ...cached,
            avatar: cached.avatar ?? cached.picture,
          });
          return;
        }
        // Cached user missing required fields — discard.
        storage.removeItem(USER_STORAGE_KEY);
      } catch {
        storage.removeItem(USER_STORAGE_KEY);
      }
    }
  }

  private clearCachedUser(): void {
    localStorage.removeItem(USER_STORAGE_KEY);
    sessionStorage.removeItem(USER_STORAGE_KEY);
  }

  private clearSession(): void {
    this.clearToken();
    this.clearCachedUser();
    this.userSignal.set(null);
    this.sessionLoaded = false;
  }

  async ensureSession(): Promise<void> {
    const token = this.getToken();

    if (!token) {
      // No token — any cached user is stale; clear and show login.
      this.clearSession();
      this.sessionLoaded = true;
      return;
    }

    if (!this.userSignal()) {
      this.restoreCachedUser();
    }

    // Token + cached user — restore for immediate paint, then verify once in background.
    if (this.userSignal()) {
      this.sessionLoaded = true;
      if (!this.backgroundVerifyDone) {
        this.backgroundVerifyDone = true;
        this.fetchCurrentUser(true).catch(() => {
          // 401 already handled inside fetchCurrentUser → clearSession.
        });
      }
      return;
    }

    if (!this.loadingPromise) {
      this.loadingPromise = (async () => {
        try {
          await this.fetchCurrentUser(true);
          this.sessionLoaded = true;
        } catch {
          if (!this.userSignal()) {
            this.sessionLoaded = true;
          }
        } finally {
          this.loadingPromise = null;
        }
      })();
    }

    await this.loadingPromise;
  }

  private async fetchCurrentUser(clearOnUnauthorized = true): Promise<User | null> {
    const token = this.getToken();
    if (!token) {
      throw new Error('No active session');
    }

    try {
      const user = await firstValueFrom(
        this.http.get<User>('/auth/me', {
          withCredentials: true,
        }),
      );

      const normalized: User = {
        ...user,
        avatar: user.picture,
      };
      this.userSignal.set(normalized);
      this.persistUser(normalized);
      return normalized;
    } catch (error) {
      if (clearOnUnauthorized && error instanceof HttpErrorResponse && error.status === 401) {
        this.clearSession();
      }
      throw error;
    }
  }

  signIn(): void {
    const currentLocation = window.location.href;
    window.location.href = `${this.configService.apiUrl()}/auth/google/login?redirect=${encodeURIComponent(currentLocation)}`;
  }

  async signOut(): Promise<void> {
    try {
      await firstValueFrom(
        this.http.post('/auth/logout', {}, {
          withCredentials: true,
        }),
      );
    } catch {
      // Ignore network errors — we'll still clear local session.
    }

    this.clearSession();
    await this.router.navigate(['/admin']).catch(() => {});
  }
}
